import React, { useState, useEffect, useRef } from "react";
import AiResult from "./AiResult";
import { getAuthHeaders } from "../utils/auth";
import PromptTemplateModal, { loadTemplates } from "./PromptTemplateModal";
import { useLanguage } from "../utils/i18n";

export default function SummaryTab({ book, api, showToast, onAddNote, result, onSetResult }) {
  const [startPage, setStartPage] = useState(1);
  const [endPage, setEndPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [followUps, setFollowUps] = useState([]);
  const [followUpInput, setFollowUpInput] = useState("");
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState(() => loadTemplates());
  const followUpRef = useRef(null);
  const { t } = useLanguage();

  const prevResult = useRef(result);
  useEffect(() => {
    if (result && result !== prevResult.current) setFollowUps([]);
    prevResult.current = result;
  }, [result]);

  const MAX_PAGES = 5;

  const handleSummarize = async () => {
    if (startPage < 1 || endPage < startPage || endPage > book.total_pages) {
      showToast(t("invalidPageRange"));
      return;
    }
    if (endPage - startPage + 1 > MAX_PAGES) {
      showToast(t("tooManyPagesSummary"));
      return;
    }
    setLoading(true);
    setFollowUps([]);
    const customTemplate = savedTemplates.summary || null;
    try {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(`${api}/summarize`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({
          book_id: book.id,
          start_page: startPage,
          end_page: endPage,
          custom_prompt_template: customTemplate,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        onSetResult(data.summary);
      } else {
        showToast(data.error || t("summaryFailed"));
      }
    } catch {
      showToast(t("failSummary"));
    }
    setLoading(false);
  };

  const sendFollowUp = async () => {
    const q = followUpInput.trim();
    if (!q || !result) return;
    setFollowUpInput("");
    setFollowUpLoading(true);
    try {
      const history = [
        { role: "user", content: `Summarize pages ${startPage}–${endPage} of my book.` },
        { role: "assistant", content: result },
        ...followUps.flatMap((fu) => [
          { role: "user", content: fu.question },
          { role: "assistant", content: fu.answer },
        ]),
        { role: "user", content: q },
      ];
      const authHeaders2 = await getAuthHeaders();
      const res = await fetch(`${api}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders2 },
        body: JSON.stringify({ messages: history, book_id: book.id }),
      });
      const data = await res.json();
      if (res.ok) {
        setFollowUps((prev) => [...prev, { question: q, answer: data.reply }]);
      } else {
        showToast(data.error || t("followUpFailed"));
      }
    } catch {
      showToast(t("failFollowUp"));
    }
    setFollowUpLoading(false);
  };

  const handleFollowUpKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !followUpLoading) { e.preventDefault(); sendFollowUp(); }
  };

  return (
    <div className="tab-content">
      {showTemplateModal && (
        <PromptTemplateModal
          modes={["summary"]}
          initialMode="summary"
          onClose={(wasSaved) => {
            setShowTemplateModal(false);
            if (wasSaved) setSavedTemplates(loadTemplates());
          }}
        />
      )}
      <div className="form-section">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <label style={{ marginBottom: 0 }}>{t("pageRangeLabel")} (1 – {book.total_pages})<span style={{ fontWeight: 400, color: "var(--text-secondary)", marginLeft: 6, fontSize: "0.8rem" }}>{t("maxPagesHint")}</span></label>
          <button
            className={`pt-trigger-btn ${savedTemplates.summary ? "has-custom" : ""}`}
            onClick={() => setShowTemplateModal(true)}
            title="Customise AI prompt template"
          >
            {t("promptBtn")}
          </button>
        </div>
        <div className="page-range">
          <input
            type="text"
            inputMode="numeric"
            value={startPage}
            onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) setStartPage(v); else if (e.target.value === "") setStartPage(""); }}
            onBlur={(e) => { const v = parseInt(e.target.value, 10); const clamped = !isNaN(v) ? Math.max(1, Math.min(v, book.total_pages)) : 1; setStartPage(clamped); setEndPage((prev) => Math.min(prev, clamped + MAX_PAGES - 1)); }}
          />
          <span>{t("pageRangeTo")}</span>
          <input
            type="text"
            inputMode="numeric"
            value={endPage}
            onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) setEndPage(v); else if (e.target.value === "") setEndPage(""); }}
            onBlur={(e) => { const v = parseInt(e.target.value, 10); const clamped = !isNaN(v) ? Math.max(startPage, Math.min(v, book.total_pages, startPage + MAX_PAGES - 1)) : startPage; setEndPage(clamped); }}
          />
        </div>
      </div>

      <button
        className="action-btn primary"
        onClick={handleSummarize}
        disabled={loading}
      >
        {loading ? (
          <div className="loading-dots"><span /><span /><span /></div>
        ) : (
          t("summarizeBtn")
        )}
      </button>

      {result && (
        <>
          <AiResult
            content={result}
            onAddNote={onAddNote ? () => onAddNote(`Summary – Pages ${startPage}–${endPage}`, result) : null}
          />

          {/* Follow-up thread */}
          {followUps.map((fu, i) => (
            <div key={i} className="followup-exchange">
              <div className="followup-question">🙋 {fu.question}</div>
              <AiResult
                content={fu.answer}
                onAddNote={onAddNote ? () => onAddNote(`Follow-up: ${fu.question}`, fu.answer) : null}
              />
            </div>
          ))}

          <div className="followup-input-row">
            <textarea
              ref={followUpRef}
              className="followup-input"
              placeholder={t("followUpPlaceholder")}
              value={followUpInput}
              rows={1}
              onChange={(e) => { setFollowUpInput(e.target.value); e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }}
              onKeyDown={handleFollowUpKey}
            />
            <button
              className="action-btn primary"
              style={{ width: "auto", padding: "8px 14px", marginTop: 0, flexShrink: 0 }}
              onClick={sendFollowUp}
              disabled={followUpLoading || !followUpInput.trim()}
            >
              {followUpLoading ? <div className="loading-dots"><span /><span /><span /></div> : t("askBtn")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
